import { Box } from '@material-ui/core';
import { FormatListNumberedOutlined, SearchOutlined, TouchApp } from '@material-ui/icons';
import * as React from 'react';
import { useMemo } from 'react';
import { useSpring } from 'react-spring';
import { AnalysisChartLoading } from '../../../AnalysisChartLoading';
import { DomainTypeGq } from '../../../../../graphql/projects/models/DomainTypeGq';
import { projectsApiService } from '../../../../../graphql/projects/projectsApiService';
import { formatNumber } from '../../../../../logic/otherUtilities/formatting/formatNumber';
import { formatTemplate } from '../../../../../logic/otherUtilities/formatting/formatTemplate';
import { ChartProps } from '../../../AnalysisList';
import { internationalText } from '../../../internationalText';
import { adjustTextColor } from '../../../Palette/adjustTextColor';
import DataTimedOut, { didQueryTimeout } from '../../DataTimedOut';
import NotEnoughData from '../../NotEnoughData';
import PerformanceItem from './PerformanceItem';

export interface PerformanceChartProps extends ChartProps { }

const PerformanceChart: React.FC<PerformanceChartProps> = ({
  project,
  analysisFilterState,
  setAnalysisFilterState,
  language,
  palette,
  skipAnimation,
  emptyOnFail,
}) => {
  // Adjust shown filters
  React.useEffect(
    () =>
      setAnalysisFilterState((prev) => ({
        ...prev,
        shownFields: {
          showTagGroupId: true,
          showTagId: true,
          showTargetRankValues: true,
        },
      })),
    []
  );

  // Data fetching
  const dataQuery = projectsApiService.queries.analysis.useGetCompetitivePerformance({
    projectId: project.id,
    domainIds: [project.domains.find(d => d.type === DomainTypeGq.PROJECT).id],
    tagGroupId: analysisFilterState?.tagGroupId,
    tagId: analysisFilterState?.tagId,
    rankValues: analysisFilterState?.targetRankValues ?? [1],
  });

  // Number animation
  // Start with 0 for initial spring
  // Fall back to 0 for interpolation to work
  const totalPerformance = dataQuery?.result;
  const clientPerformance = useMemo(
    () =>
      dataQuery.result?.domainPerformanceAnalyses.find(
        (d) => d.domain.type === DomainTypeGq.PROJECT
      ),
    [dataQuery.result?.domainPerformanceAnalyses ?? 1]
  );

  const normalNumbers = {
    totalKeywordCount: totalPerformance?.totalKeywordCount ?? 0,
    totalSearchVolume: totalPerformance?.totalSearchVolume ?? 0,
    rank1Traffic: Math.max(totalPerformance?.totalTraffic ?? 0, clientPerformance?.traffic ?? 0),
    clientKeywordCount: clientPerformance?.coveredKeywordCount ?? 0,
    clientSearchVolume: clientPerformance?.coveredSearchVolume ?? 0,
    clientTraffic: clientPerformance?.traffic ?? 0,
    clientKeywordCountShare: Math.min(
      100,
      ((clientPerformance?.coveredKeywordCount ?? 0) /
        (totalPerformance?.totalKeywordCount > 0 ? totalPerformance?.totalKeywordCount : 1)) *
      100
    ),
    clientSearchVolumeShare: Math.min(
      100,
      ((clientPerformance?.coveredSearchVolume ?? 0) /
        (totalPerformance?.totalSearchVolume > 0 ? totalPerformance?.totalSearchVolume : 1)) *
      100
    ),
    clientPotentialShare: Math.min(
      100,
      ((clientPerformance?.traffic ?? 0) /
        (totalPerformance?.totalTraffic > 0 ? totalPerformance?.totalTraffic : 1)) *
      100
    ),
  };

  const localSkipAnimation = sessionStorage.getItem('skip_animation') || skipAnimation; // Setting for skipping spring when screenshotting
  const springNumbers = useSpring({
    ...normalNumbers,
    from: {
      ...normalNumbers,
      clientKeywordCount: 0,
      clientSearchVolume: 0,
      clientTraffic: 0,
    },
    immediate: localSkipAnimation ? true : undefined,
  });
  const textColor = adjustTextColor(palette, 0.85, 0);

  if (didQueryTimeout(dataQuery?.query)) {
    return emptyOnFail ? null : <DataTimedOut />;
  }

  if (dataQuery?.query.loading) {
    return <AnalysisChartLoading />;
  }

  if (!clientPerformance) {
    return emptyOnFail ? null : <NotEnoughData />;
  }

  return (
    <div data-screenshot-id="chart-loaded">
      <div className={'h-[500px] w-full flex justify-between'}>
        <div className="w-full p-8">
          <PerformanceItem
            number={springNumbers.clientKeywordCountShare}
            share={normalNumbers.clientKeywordCountShare}
            label={internationalText.chart.performance.rankings.label[language]}
            numberSuffix="%"
            numberLabel={internationalText.chart.performance.rankings.number_label[language]}
            icon={FormatListNumberedOutlined}
            bgColor={palette.tertiaryScale[1]}
            labelColor={textColor}
            description={[
              formatTemplate(internationalText.chart.performance.rankings.desc_1[language], [
                clientPerformance?.domain.hostName,
              ]),
              formatTemplate(internationalText.chart.performance.rankings.desc_2[language], [
                formatNumber(clientPerformance?.coveredKeywordCount),
                formatNumber(totalPerformance.totalKeywordCount),
              ]),
              internationalText.chart.performance.rankings.desc_3[language],
            ]}
          />
        </div>
        <div className="w-full p-8">
          <PerformanceItem
            number={springNumbers.clientSearchVolumeShare}
            share={normalNumbers.clientSearchVolumeShare}
            label={internationalText.chart.performance.reach.label[language]}
            numberSuffix="%"
            numberLabel={internationalText.chart.performance.reach.number_label[language]}
            icon={SearchOutlined}
            bgColor={palette.secondaryScale[1]}
            labelColor={textColor}
            description={[
              formatTemplate(internationalText.chart.performance.reach.desc_1[language], [
                clientPerformance?.domain.hostName,
              ]),
              formatTemplate(internationalText.chart.performance.reach.desc_2[language], [
                formatNumber(clientPerformance?.coveredSearchVolume),
                formatNumber(totalPerformance.totalSearchVolume),
              ]),
              internationalText.chart.performance.reach.desc_3[language],
            ]}
          />
        </div>
        <div className="w-full p-8">
          <PerformanceItem
            number={springNumbers.clientTraffic}
            share={normalNumbers.clientPotentialShare}
            label={internationalText.chart.performance.traffic.label[language]}
            numberLabel={internationalText.chart.performance.traffic.number_label[language]}
            icon={TouchApp}
            labelColor={textColor}
            bgColor={palette.tertiaryScale[1]}
            description={[
              formatTemplate(internationalText.chart.performance.traffic.desc_1[language], [
                clientPerformance?.domain.hostName,
              ]),
              formatTemplate(internationalText.chart.performance.traffic.desc_2[language], [
                formatNumber(clientPerformance?.traffic),
                formatNumber(
                  Math.max(totalPerformance?.totalTraffic ?? 0, clientPerformance?.traffic ?? 0)
                ),
              ]),
              internationalText.chart.performance.traffic.desc_3[language],
            ]}
          />
        </div>
      </div>
    </div>
  );
};

export default PerformanceChart;
